/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import IdleTimer from "react-idle-timer";
import {
  Layout,
  Menu,
  Avatar,
  Dropdown,
  Typography,
  Button,
  Input,
  Modal,
  Tooltip,
  message,
  Tag,
  Badge,
  List,
} from "antd";
import {
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  BellOutlined,
  DownOutlined,
  SearchOutlined,
  ToolOutlined,
  TeamOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from "@ant-design/icons";
import { logout } from "../../services/authService";
import { getSessionValue, clearSession } from "../../security/session";
import { useAlerts } from "../notifications/AlertsContext";

const { Sider, Header, Content } = Layout;
const { Text } = Typography;
const AUTO_LOGOUT_TIMEOUT_MS = 10 * 60 * 1000;

const ROLE_LABELS = {
  Operario:       "Operário",
  Director:       "Administrador",
  Contabilidade:  "Contabilidade",
  recargatest:    "Recargas",
  Comercial:      "Comercial",
  Tecnico:        "Técnico",
  IRS:            "IRS",
};

const ROLE_COLORS = {
  Director:       "blue",
  Operario:       "teal",
  Contabilidade:  "purple",
  recargatest:    "green",
  Comercial:      "cyan",
  Tecnico:        "volcano",
};

/**
 * AppLayout — shared shell for all role dashboards.
 *
 * Props:
 *   navItems       – Ant Design Menu items array
 *   onNavClick     – (menuKey) => void, called when a nav item is clicked
 *   defaultKey     – initially selected menu key (string)
 *   pageTitle      – displayed in the header
 *   profilePath    – path to push for profile page (e.g. "/home/Perfil")
 *   children       – the <Switch>…</Switch> of protected routes
 */
export default function AppLayout({
  navItems = [],
  onNavClick,
  defaultKey = "1",
  pageTitle = "Dashboard",
  profilePath,
  children,
}) {
  const history = useHistory();
  const idleRef = useRef(null);
  const alerts = useAlerts();

  const [collapsed, setCollapsed] = useState(false);
  const [currentTitle, setCurrentTitle] = useState(pageTitle);
  const [quickSearchOpen, setQuickSearchOpen] = useState(false);
  const [quickSearchValue, setQuickSearchValue] = useState("");

  const username = (getSessionValue("username", "Utilizador") || "Utilizador").trim();
  const grupo = getSessionValue("grupo", "");
  const roleLabel = ROLE_LABELS[grupo] || grupo;
  const roleColor = ROLE_COLORS[grupo] || "default";

  const cleanLogout = () => {
    clearSession();
    history.push("/");
  };

  const [loggingOut, setLoggingOut] = useState(false);

  const doLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout();
      message.success("Sessão terminada com sucesso.");
    } catch (e) {
      const s = e?.response?.status;
      if (s && s !== 401) {
        message.warning("Sessão terminada no browser.");
      }
    } finally {
      setLoggingOut(false);
      cleanLogout();
    }
  };

  const onIdle = () => {
    message.info("Sessão expirada por inatividade.");
    cleanLogout();
  };

  const findLabel = (items, key) => {
    for (const item of items) {
      if (item?.key === key) return item.label;
      if (item?.children) {
        const found = findLabel(item.children, key);
        if (found) return found;
      }
    }
    return null;
  };

  const handleMenuClick = ({ key }) => {
    const label = findLabel(navItems, key);
    if (label && typeof label === "string") setCurrentTitle(label);
    if (onNavClick) onNavClick(key);
  };

  // Quando o título vem da rota (e não de um clique no menu), acompanha-a —
  // é o que mantém o cabeçalho certo ao chegar por URL ou por link interno.
  useEffect(() => {
    setCurrentTitle(pageTitle);
  }, [pageTitle]);

  const openQuickSearch = () => {
    setQuickSearchOpen(true);
    setQuickSearchValue("");
  };

  const submitQuickSearch = (type) => {
    const query = quickSearchValue.trim();
    if (!query) {
      message.warning("Informe o número para pesquisar.");
      return;
    }

    setQuickSearchOpen(false);
    if (type === "contador") {
      history.push({
        pathname: "/home/gest-contadores/pesquisar-contador",
        state: { quickSearchType: "contador", query },
      });
      return;
    }

    history.push({
      pathname: "/home/gest-cliente/pesquisar-cliente",
      state: { quickSearchType: "cliente", query },
    });
  };

  const userDropdown = {
    items: [
      {
        key: "user-info",
        label: (
          <div style={{ padding: "4px 0", pointerEvents: "none" }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "var(--text)" }}>
              {username}
            </div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 1 }}>
              {roleLabel}
            </div>
          </div>
        ),
      },
      { type: "divider" },
      // Todos os papéis têm perfil — a conta é do utilizador, não do papel.
      // A alteração de palavra-passe vive lá dentro, no separador Segurança.
      ...(profilePath
        ? [
            {
              key: "perfil",
              icon: <SettingOutlined />,
              label: "Perfil & definições",
              onClick: () => history.push(profilePath),
            },
          ]
        : []),
      { type: "divider" },
      {
        key: "logout",
        icon: <LogoutOutlined />,
        label: loggingOut ? "A terminar sessão..." : "Terminar sessão",
        onClick: doLogout,
        disabled: loggingOut,
        danger: true,
      },
    ],
  };

  return (
    <Layout className="mwa-layout" style={{ height: "100vh", overflow: "hidden" }}>
      {/* ══════════════ SIDEBAR ══════════════ */}
      <Sider
        className="mwa-sider"
        collapsed={collapsed}
        onCollapse={setCollapsed}
        collapsible
        trigger={null}
        width={240}
        collapsedWidth={64}
        theme="dark"
      >
        {/* Logo */}
        <div className="mwa-logo-wrap">
          <div className="mwa-logo-block">
            <img
              src={require("../images/ENH.png")}
              alt="ENH KOGAS"
            />
            {!collapsed && (
              <div className="mwa-logo-text">
                <span className="logo-name">ENH · KOGAS</span>
                <span className="logo-sub">{roleLabel}</span>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <Menu
          className="mwa-menu"
          mode="inline"
          theme="dark"
          defaultSelectedKeys={[defaultKey]}
          onClick={handleMenuClick}
          items={navItems}
        />

        {/* Bottom: collapse toggle */}
        <div className="mwa-sider-footer">
          <Button
            type="text"
            className="mwa-collapse-btn"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
          >
            {!collapsed && <span>Recolher menu</span>}
          </Button>
        </div>
      </Sider>

      {/* ══════════════ MAIN ══════════════ */}
      <Layout className="mwa-main" style={{ overflow: "hidden" }}>

        {/* ── Header ── */}
        <Header className="mwa-header">
          <div className="mwa-header-inner">

            {/* Left: page title */}
            <div className="mwa-header-left">
              <span className="mwa-page-title">{currentTitle}</span>
              <span className="mwa-breadcrumb-small">
                ENH KOGAS &rsaquo; {roleLabel} &rsaquo; {currentTitle}
              </span>
            </div>

            {/* Right: actions */}
            <div className="mwa-header-right">

              {/* Notifications */}
              <Tooltip title="Pesquisa rápida">
                <Button
                  className="mwa-icon-btn"
                  icon={<SearchOutlined />}
                  onClick={openQuickSearch}
                />
              </Tooltip>

              {/* O sino só acende quando há mesmo alguma coisa. Sem provedor de
                  alertas para este perfil, fica um botão sem indicador — um
                  ponto vermelho permanente ensina o utilizador a ignorá-lo. */}
              <Dropdown
                trigger={["click"]}
                disabled={!alerts || alerts.total === 0}
                dropdownRender={() => (
                  <div className="mwa-notif-panel">
                    <div className="mwa-notif-panel-title">Por abrir</div>
                    <List
                      size="small"
                      dataSource={(alerts && alerts.items) || []}
                      renderItem={(item) => (
                        <List.Item className={item.urgent ? "mwa-notif-item mwa-notif-item--urgent" : "mwa-notif-item"}>
                          <List.Item.Meta title={item.title} description={item.subtitle} />
                        </List.Item>
                      )}
                    />
                    {alerts && alerts.onOpenAll && (
                      <Button type="link" block onClick={alerts.onOpenAll}>Ver todos</Button>
                    )}
                  </div>
                )}
              >
                <Tooltip title="Notificações">
                  <div className="mwa-notif-wrap">
                    <Badge count={(alerts && alerts.total) || 0} size="small" offset={[-2, 2]}>
                      <Button className="mwa-icon-btn" icon={<BellOutlined />} />
                    </Badge>
                  </div>
                </Tooltip>
              </Dropdown>

              <div className="mwa-header-sep" />

              {/* User pill */}
              <Dropdown
                menu={userDropdown}
                trigger={["click"]}
                placement="bottomRight"
              >
                <div className="mwa-user-pill" role="button" tabIndex={0}>
                  <div className="mwa-avatar-wrap">
                    <Avatar className="mwa-avatar">
                      {username.charAt(0).toUpperCase()}
                    </Avatar>
                    <span className="mwa-status-dot" />
                  </div>
                  <div className="mwa-user-info">
                    <span className="mwa-user-name">{username}</span>
                    <span className="mwa-user-sub">{roleLabel}</span>
                  </div>
                  <DownOutlined className="mwa-chevron" />
                </div>
              </Dropdown>
            </div>
          </div>
        </Header>

        {/* ── Content ── */}
        <Content className="mwa-content">
          <div className="mwa-content-inner">
            <IdleTimer ref={idleRef} timeout={AUTO_LOGOUT_TIMEOUT_MS} onIdle={onIdle} />
            {children}
          </div>
        </Content>
      </Layout>

      <Modal
        title="Pesquisa rápida"
        open={quickSearchOpen}
        onCancel={() => setQuickSearchOpen(false)}
        footer={null}
        width={460}
        className="quick-search-modal"
        destroyOnClose
      >
        <div className="quick-search-box">
          <Input
            autoFocus
            size="large"
            value={quickSearchValue}
            onChange={(e) => setQuickSearchValue(e.target.value.replace(/\D/g, ""))}
            onPressEnter={() => submitQuickSearch("cliente")}
            placeholder="Número do cliente ou contador"
            prefix={<SearchOutlined />}
          />
          <div className="quick-search-actions">
            <Button
              className="quick-search-action"
              icon={<TeamOutlined />}
              onClick={() => submitQuickSearch("cliente")}
            >
              Cliente
            </Button>
            <Button
              className="quick-search-action"
              icon={<ToolOutlined />}
              onClick={() => submitQuickSearch("contador")}
            >
              Contador
            </Button>
          </div>
          <p className="quick-search-hint">
            A pesquisa abre a página correta e usa a mesma lógica já existente do MPS.
          </p>
        </div>
      </Modal>
    </Layout>
  );
}
